import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { queryKeys } from "@/lib/queryKeys";

export interface IdentityConnection {
  provider: "atlassian" | "google";
  connected: boolean;
}

interface PlatformStatus {
  connected: boolean;
}

interface OnboardingStatusResponse {
  user_key: string;
  registered: boolean;
  platforms: Record<string, PlatformStatus>;
}

export function useIdentityConnections() {
  const { user } = useAuth();
  const email = user?.email ?? null;

  const query = useQuery({
    queryKey: queryKeys.identity.connections(email ?? ""),
    queryFn: async () => {
      const data = await api<OnboardingStatusResponse>(
        `/api/v2/identity/onboarding-status?user_key=${encodeURIComponent(email!)}`,
      );
      return (["atlassian", "google"] as const).map((provider) => ({
        provider,
        connected: data.platforms[provider]?.connected ?? false,
      }));
    },
    enabled: email !== null,
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    connections: query.data ?? [],
    loading: query.isLoading,
    error: query.error ? "Failed to load identity connections" : null,
    refetch,
  };
}
